import { useEffect } from 'react';

import type { CoachAvailability } from '@/modules/api';

import { useRulesEngine } from './useRulesEngine';

export type UseCoachAvailability = {
  availability: CoachAvailability | undefined;
  isLoading: boolean;
};

/**
 * Reads a single provider's initial-consult availability from the rules-engine
 * cache, kicking the (deduped) fetch on mount. Backs both the matched-coach
 * cards' "next appointment" line and the availability screen — because the
 * cache is shared, the availability screen reuses what the cards already
 * fetched instead of re-requesting. `isLoading` is true until that provider's
 * entry settles (so callers can show a skeleton).
 */
export function useCoachAvailability(providerUuid: string | undefined): UseCoachAvailability {
  const { coachAvailability, ensureCoachAvailability } = useRulesEngine();

  useEffect(() => {
    if (providerUuid) ensureCoachAvailability(providerUuid);
  }, [providerUuid, ensureCoachAvailability]);

  const state = providerUuid ? coachAvailability[providerUuid] : undefined;
  return {
    availability: state?.status === 'ready' ? state.value : undefined,
    isLoading: providerUuid
      ? !state || state.status === 'loading' || state.status === 'unset'
      : false,
  };
}
